import React from 'react';

const InstitutionalStrategies = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Market Making Fundamentals",
      icon: "🏛️",
      content: {
        keyPoints: [
          "Provide liquidity by quoting bid-ask spreads",
          "Profit from bid-ask spreads and inventory management",
          "Manage inventory risk through delta hedging",
          "High-frequency operations with tight risk controls"
        ],
        example: "Quote BTC $50k calls: Bid $2,000, Ask $2,050. Collect $50 spread per trade, hedge delta risk immediately."
      },
      quiz: [
        {
          id: 'q1',
          question: "Market makers primarily profit from:",
          options: ["Directional moves", "Bid-ask spreads", "Time decay", "Volatility increases"],
          correct: 1,
          explanation: "Correct! Market makers profit from the bid-ask spread by buying at bid and selling at ask."
        },
        {
          id: 'q2',
          question: "What's critical for market maker risk management?",
          options: ["High leverage", "Delta hedging", "Directional bets", "Long holding periods"],
          correct: 1,
          explanation: "Correct! Delta hedging is essential to manage inventory risk from providing liquidity."
        }
      ]
    },
    2: {
      title: "Statistical Arbitrage",
      icon: "📊",
      content: {
        keyPoints: [
          "Exploit statistical relationships between assets",
          "Mean reversion and momentum strategies",
          "Risk-neutral profit opportunities",
          "High Sharpe ratio, low drawdown strategies"
        ],
        example: "BTC-ETH correlation breaks down: Long underperforming asset, short outperforming, profit on convergence."
      },
      quiz: [
        {
          id: 'q1',
          question: "Statistical arbitrage relies on:",
          options: ["Random price movements", "Statistical relationships", "Market timing", "News events"],
          correct: 1,
          explanation: "Correct! Statistical arbitrage exploits predictable statistical relationships between assets."
        },
        {
          id: 'q2',
          question: "The goal of statistical arbitrage is:",
          options: ["Maximum leverage", "Risk-neutral profits", "Directional exposure", "Long-term holding"],
          correct: 1,
          explanation: "Correct! Statistical arbitrage aims for risk-neutral profits from statistical mispricings."
        }
      ]
    },
    3: {
      title: "Volatility Surface Trading",
      icon: "🌊",
      content: {
        keyPoints: [
          "Trade relative volatility across strikes and terms",
          "Identify vol surface anomalies and mispricings",
          "Calendar spreads and vol term structure trades",
          "Cross-strike volatility arbitrage"
        ],
        example: "30-day vol trading at 60%, 60-day at 55%. Sell 30-day, buy 60-day to profit from term structure inversion."
      },
      quiz: [
        {
          id: 'q1',
          question: "Volatility surface trading focuses on:",
          options: ["Directional price moves", "Relative volatility mispricings", "Time decay only", "Strike price selection"],
          correct: 1,
          explanation: "Correct! Vol surface trading exploits relative volatility mispricings across strikes and terms."
        },
        {
          id: 'q2',
          question: "Term structure trades involve:",
          options: ["Same expiration options", "Different expiration volatilities", "Strike price spreads", "Underlying assets"],
          correct: 1,
          explanation: "Correct! Term structure trades exploit volatility differences across expiration dates."
        }
      ]
    },
    4: {
      title: "Cross-Asset Hedging",
      icon: "🔗",
      content: {
        keyPoints: [
          "Hedge crypto options with traditional assets",
          "Correlation-based hedging strategies",
          "Multi-asset portfolio optimization",
          "Basis risk management across markets"
        ],
        example: "Hedge BTC options exposure using SPY puts when BTC-SPY correlation is high during market stress."
      },
      quiz: [
        {
          id: 'q1',
          question: "Cross-asset hedging requires understanding:",
          options: ["Individual asset risks only", "Asset correlations", "Single market dynamics", "Time decay patterns"],
          correct: 1,
          explanation: "Correct! Cross-asset hedging relies on understanding correlations between different asset classes."
        },
        {
          id: 'q2',
          question: "Basis risk in cross-asset hedging refers to:",
          options: ["Interest rate changes", "Correlation breakdown", "Time decay", "Strike selection"],
          correct: 1,
          explanation: "Correct! Basis risk occurs when correlations break down, making cross-asset hedges less effective."
        }
      ]
    },
    5: {
      title: "Large Order Management",
      icon: "📦",
      content: {
        keyPoints: [
          "Execute large orders without market impact",
          "TWAP and VWAP execution strategies",
          "Iceberg orders and hidden liquidity",
          "Optimal execution timing and sizing"
        ],
        example: "Split 1000 BTC option order into 50-lot parcels over 2 hours to minimize market impact."
      },
      quiz: [
        {
          id: 'q1',
          question: "Large order management aims to:",
          options: ["Maximize speed", "Minimize market impact", "Increase visibility", "Concentrate execution"],
          correct: 1,
          explanation: "Correct! The goal is to minimize market impact when executing large orders."
        },
        {
          id: 'q2',
          question: "TWAP stands for:",
          options: ["Total Weighted Average Price", "Time Weighted Average Price", "Trade Weighted Asset Price", "Timed Weighted Allocation Price"],
          correct: 1,
          explanation: "Correct! TWAP is Time Weighted Average Price, an execution strategy that spreads orders over time."
        }
      ]
    },
    6: {
      title: "Risk Management Systems",
      icon: "🛡️",
      content: {
        keyPoints: [
          "Real-time portfolio risk monitoring",
          "VAR and stress testing frameworks",
          "Position limits and exposure controls",
          "Automated risk alerts and circuit breakers"
        ],
        example: "System automatically hedges when portfolio delta exceeds ±500 or VaR breaches 2% limit."
      },
      quiz: [
        {
          id: 'q1',
          question: "VAR (Value at Risk) measures:",
          options: ["Guaranteed profits", "Potential losses at confidence level", "Average returns", "Maximum profits"],
          correct: 1,
          explanation: "Correct! VAR estimates potential losses at a given confidence level over a specific time period."
        },
        {
          id: 'q2',
          question: "Circuit breakers in risk systems:",
          options: ["Increase leverage", "Automatically halt risky activity", "Boost returns", "Ignore limits"],
          correct: 1,
          explanation: "Correct! Circuit breakers automatically halt trading when risk limits are breached."
        }
      ]
    },
    7: {
      title: "Flow Trading Strategies",
      icon: "🌊",
      content: {
        keyPoints: [
          "Trading against client flow profitably",
          "Internalization vs market execution decisions",
          "Flow toxicity analysis and client segmentation",
          "Dynamic pricing based on flow characteristics"
        ],
        example: "Retail client buys BTC calls: Internalize trade, hedge delta, profit from time decay and vol edge."
      },
      quiz: [
        {
          id: 'q1',
          question: "Flow trading involves:",
          options: ["Random market orders", "Trading against client flow", "Technical analysis only", "Long-term investing"],
          correct: 1,
          explanation: "Correct! Flow trading involves profitably taking the other side of client orders."
        },
        {
          id: 'q2',
          question: "Flow toxicity refers to:",
          options: ["Environmental concerns", "Informed client trading", "Market volatility", "Regulatory issues"],
          correct: 1,
          explanation: "Correct! Toxic flow comes from informed traders who may have adverse selection against market makers."
        }
      ]
    },
    8: {
      title: "Structured Products",
      icon: "🏗️",
      content: {
        keyPoints: [
          "Design complex payoff structures for clients",
          "Combine multiple options and underlying assets",
          "Risk decomposition and pricing",
          "Regulatory and operational considerations"
        ],
        example: "Create BTC autocallable note: Principal protection + leveraged upside participation with early redemption features."
      },
      quiz: [
        {
          id: 'q1',
          question: "Structured products typically:",
          options: ["Use single instruments", "Combine multiple derivatives", "Avoid complexity", "Focus on one risk factor"],
          correct: 1,
          explanation: "Correct! Structured products combine multiple derivatives to create specific payoff profiles."
        },
        {
          id: 'q2',
          question: "Key consideration in structured product design:",
          options: ["Maximum complexity", "Client risk-return objectives", "Regulatory avoidance", "Cost minimization only"],
          correct: 1,
          explanation: "Correct! Products must match client risk-return objectives while being properly priced."
        }
      ]
    },
    9: {
      title: "Prime Brokerage Operations",
      icon: "🏦",
      content: {
        keyPoints: [
          "Multi-venue execution and clearing",
          "Securities lending and financing",
          "Margin and capital optimization",
          "Operational risk and settlement"
        ],
        example: "Provide hedge fund with unified margin across 5 exchanges, securities lending, and consolidated reporting."
      },
      quiz: [
        {
          id: 'q1',
          question: "Prime brokerage provides:",
          options: ["Simple execution only", "Comprehensive trading services", "Basic clearing", "Individual accounts only"],
          correct: 1,
          explanation: "Correct! Prime brokerage offers comprehensive trading, clearing, financing, and operational services."
        },
        {
          id: 'q2',
          question: "Securities lending in prime brokerage:",
          options: ["Is prohibited", "Generates additional revenue", "Only for banks", "Reduces risk"],
          correct: 1,
          explanation: "Correct! Securities lending allows prime brokers to generate additional revenue from client positions."
        }
      ]
    },
    10: {
      title: "Regulatory and Compliance",
      icon: "⚖️",
      content: {
        keyPoints: [
          "Position reporting and transparency requirements",
          "Capital adequacy and leverage restrictions",
          "Market abuse prevention and surveillance",
          "Cross-jurisdictional compliance frameworks"
        ],
        example: "Implement real-time position reporting, maintain 15% capital ratio, monitor for manipulation patterns."
      },
      quiz: [
        {
          id: 'q1',
          question: "Regulatory compliance in options trading requires:",
          options: ["Ignoring rules", "Comprehensive monitoring systems", "Minimal documentation", "Self-regulation only"],
          correct: 1,
          explanation: "Correct! Compliance requires sophisticated monitoring, reporting, and control systems."
        },
        {
          id: 'q2',
          question: "Capital adequacy rules aim to:",
          options: ["Maximize leverage", "Ensure financial stability", "Reduce profitability", "Eliminate trading"],
          correct: 1,
          explanation: "Correct! Capital adequacy rules ensure institutions can withstand losses and maintain stability."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Institutional-grade trading strategies</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-indigo-500 pl-4">
            <h4 className="text-white font-bold mb-2">🏛️ Professional Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-violet-500/20 border border-violet-500/50 rounded-lg p-4">
            <h5 className="text-violet-400 font-bold mb-2">🎯 Institutional Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Institutional Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these institutional strategies</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-indigo-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `institutional_strategies_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Institutional Trader'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'institutional_strategies'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Institutional Strategies Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default InstitutionalStrategies;
